import sumBy from 'lodash/sumBy';
import maxBy from 'lodash/maxBy';
import { approvalStatus } from '../../utils/approvalStatus.js';

export const HOURS_TABS = Object.freeze({
  OPEN: 'open',
  PROCESSED: 'processed'
});

const APPROVED = 'approved';

const isEntered = detail => detail.id !== null;
const isSavedAsApproved = detail => detail.savedStatus === APPROVED;
const isExported = detail => isSavedAsApproved(detail) && Boolean(detail.exported_at);
const isBlankOutsidePlacement = detail => detail.outsidePlacement && !isEntered(detail);
const isOpenDay = detail => !isExported(detail) && !isBlankOutsidePlacement(detail);
const isEnteredOpenDay = detail => isEntered(detail) && !isExported(detail);
const awaitsExportOrEntry = details => details.some(isEnteredOpenDay) || !details.some(isEntered);
const hasExportedDay = details => details.some(isExported);
const NO_APPROVED_HOURS_TO_EXPORT = 'Er zijn geen goedgekeurde uren om te exporteren.';
const PROCESSED_HOURS_ARE_EXPORTED_ALREADY = 'Verwerkte uren zijn al geëxporteerd.';

export function holdsHoursToExport(rows) {
  return rows.some(row => row._hoursDetails.some(isSavedAsApproved));
}

export const TAB_DEFINITIONS = Object.freeze([
  {
    id: HOURS_TABS.OPEN,
    label: 'Openstaand',
    emptyMessage: 'Geen openstaande uren voor deze week',
    exportBlockedReasonFor: rows => (holdsHoursToExport(rows) ? null : NO_APPROVED_HOURS_TO_EXPORT),
    locksDays: false,
    holdsRow: awaitsExportOrEntry,
    holdsDay: isOpenDay
  },
  {
    id: HOURS_TABS.PROCESSED,
    label: 'Verwerkt',
    emptyMessage: 'Geen verwerkte uren voor deze week',
    exportBlockedReasonFor: () => PROCESSED_HOURS_ARE_EXPORTED_ALREADY,
    locksDays: true,
    holdsRow: hasExportedDay,
    holdsDay: isExported
  }
]);

export function tabDefinition(tab) {
  return TAB_DEFINITIONS.find(definition => definition.id === tab);
}

export function sumOf(details, field) {
  return sumBy(details, detail => Number(detail[field] || 0));
}

function approvedHoursOf(details) {
  return sumBy(details.filter(isSavedAsApproved), detail => Number(detail.hours) + Number(detail.overtime));
}

function latestApproverOf(details) {
  const latestApproval = maxBy(
    details.filter(detail => isSavedAsApproved(detail) && detail.approved_by),
    detail => new Date(detail.updated_at).getTime()
  );

  return latestApproval ? latestApproval.approved_by.name : null;
}

export function rowsForTab(rows, tab) {
  const { holdsRow, holdsDay } = tabDefinition(tab);

  return rows.reduce((tabRows, row) => {
    const details = row._hoursDetails.filter(holdsDay);

    if (holdsRow(row._hoursDetails) && details.length) {
      tabRows.push({
        ...row,
        approvedHours: approvedHoursOf(details),
        approved_by: latestApproverOf(details),
        approvalStatus: approvalStatus(details),
        _hoursDetails: details
      });
    }

    return tabRows;
  }, []);
}

export function tabRowsOf(rows) {
  return Object.freeze(TAB_DEFINITIONS.reduce((rowsByTab, { id }) => ({
    ...rowsByTab,
    [id]: Object.freeze(rowsForTab(rows, id).map(row => Object.freeze(row)))
  }), {}));
}
